import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  Segmented,
  Table,
  type MenuProps,
  type TableColumnType,
  type TableProps,
} from 'antd';
import {
  Columns3,
  Download,
  Settings2,
  X,
} from 'lucide-react';
import { useMemo, useState, type Key, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../EmptyState';

export type Density = 'compact' | 'comfortable' | 'cozy';

export interface DataTableBulkAction<T> {
  key: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  onClick: (rows: T[]) => void | Promise<void>;
}

export interface DataTableProps<T> {
  data: readonly T[];
  columns: TableColumnType<T>[];
  rowKey: keyof T | ((row: T) => Key);
  loading?: boolean;
  pagination?: {
    page: number;
    size: number;
    total: number;
    onChange: (page: number, size: number) => void;
  };
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
  };
  filters?: ReactNode;
  extra?: ReactNode;
  bulkActions?: DataTableBulkAction<T>[];
  exportable?: boolean;
  exportFileName?: string;
  densityToggle?: boolean;
  columnVisibility?: boolean;
  emptyState?: ReactNode;
  scrollX?: number | 'max-content';
}

function toKeyFn<T>(rowKey: DataTableProps<T>['rowKey']): (row: T) => Key {
  if (typeof rowKey === 'function') return rowKey;
  return (row: T) => row[rowKey] as Key;
}

function safeStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function downloadCsv<T>(rows: readonly T[], columns: TableColumnType<T>[], fileName: string) {
  const visible = columns.filter((c) => c.dataIndex !== undefined || c.key !== undefined);
  const header = visible.map((c) => safeStr(c.title) || safeStr(c.key) || safeStr(c.dataIndex)).join(';');
  const lines = rows.map((row) =>
    visible
      .map((c) => {
        const key = (c.dataIndex ?? c.key) as keyof T;
        const value = row[key];
        const text = safeStr(value);
        return `"${text.replace(/"/g, '""')}"`;
      })
      .join(';'),
  );
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function DataTable<T extends object>({
  data,
  columns,
  rowKey,
  loading,
  pagination,
  search,
  filters,
  extra,
  bulkActions,
  exportable,
  exportFileName = 'export',
  densityToggle = true,
  columnVisibility = true,
  emptyState,
  scrollX = 'max-content',
}: DataTableProps<T>) {
  const { t } = useTranslation(['components', 'common']);
  const [density, setDensity] = useState<Density>('comfortable');
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Key[]>([]);

  const keyFn = toKeyFn(rowKey);

  const filteredColumns = useMemo(
    () => columns.filter((c) => !hiddenCols.has((safeStr(c.key) || safeStr(c.dataIndex)))),
    [columns, hiddenCols],
  );

  const visibilityItems: MenuProps['items'] = useMemo(
    () =>
      columns
        .filter((c) => c.title !== undefined)
        .map((c) => {
          const key = (safeStr(c.key) || safeStr(c.dataIndex));
          return {
            key,
            label: (
              <Checkbox
                checked={!hiddenCols.has(key)}
                onChange={() => {
                  setHiddenCols((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }}
              >
                {safeStr(c.title)}
              </Checkbox>
            ),
          };
        }),
    [columns, hiddenCols],
  );

  const tableSize: TableProps<T>['size'] =
    density === 'compact' ? 'small' : density === 'cozy' ? 'large' : 'middle';

  const selectedRows = useMemo(
    () => data.filter((row) => selected.includes(keyFn(row))),
    [data, selected, keyFn],
  );

  return (
    <div className="ds-table">
      <div className="ds-table__toolbar" role="toolbar" aria-label={t('dataTable.toolbarLabel')}>
        <div className="ds-table__toolbar-left">
          {search ? (
            <Input.Search
              className="ds-table__search"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder ?? t('dataTable.searchPlaceholder')}
              allowClear
            />
          ) : null}
          {filters}
        </div>
        <div className="ds-table__toolbar-right">
          {extra}
          {densityToggle ? (
            <Segmented
              size="small"
              value={density}
              onChange={(v) => setDensity(v as Density)}
              options={[
                { value: 'compact', label: t('dataTable.density.compact') },
                { value: 'comfortable', label: t('dataTable.density.comfortable') },
                { value: 'cozy', label: t('dataTable.density.cozy') },
              ]}
            />
          ) : null}
          {columnVisibility ? (
            <Dropdown menu={{ items: visibilityItems }} trigger={['click']}>
              <Button icon={<Columns3 size={14} />} size="small">
                {t('dataTable.columns')}
              </Button>
            </Dropdown>
          ) : null}
          {exportable ? (
            <Button
              icon={<Download size={14} />}
              size="small"
              onClick={() => downloadCsv(data, filteredColumns, exportFileName)}
            >
              CSV
            </Button>
          ) : null}
        </div>
      </div>

      {selected.length > 0 && bulkActions && bulkActions.length > 0 ? (
        <div className="ds-table__bulkbar" role="region" aria-label={t('dataTable.bulkbarLabel')}>
          <span>
            <strong>{selected.length}</strong> {t('dataTable.rowsSelected')}
          </span>
          <div className="ds-table__bulkbar-actions">
            {bulkActions.map((a) => (
              <Button
                key={a.key}
                size="small"
                danger={a.danger}
                icon={a.icon}
                onClick={() => void a.onClick(selectedRows)}
              >
                {a.label}
              </Button>
            ))}
            <Button
              size="small"
              type="text"
              icon={<X size={14} />}
              onClick={() => setSelected([])}
              aria-label={t('dataTable.clearSelection')}
            />
          </div>
        </div>
      ) : null}

      <Table<T>
        rowKey={keyFn}
        loading={loading}
        size={tableSize}
        dataSource={[...data]}
        columns={filteredColumns}
        rowSelection={
          bulkActions && bulkActions.length > 0
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys),
                preserveSelectedRowKeys: true,
              }
            : undefined
        }
        scroll={{ x: scrollX }}
        pagination={
          pagination
            ? {
                current: pagination.page,
                pageSize: pagination.size,
                total: pagination.total,
                onChange: pagination.onChange,
                showSizeChanger: true,
                showTotal: (total, [start, end]) => `${start}-${end} / ${total}`,
              }
            : false
        }
        locale={{
          emptyText:
            emptyState ??
            (loading ? (
              ' '
            ) : (
              <EmptyState
                icon={<Settings2 size={32} />}
                title={t('dataTable.emptyTitle')}
                description={t('dataTable.emptyDescription')}
              />
            )),
        }}
      />
    </div>
  );
}
